"use client";

import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { FormHeading, SuccessMessageType } from "@/interface/types";

const EnqueryForm = dynamic(() => import("@/components/forms/EnqueryForm"), {
  ssr: false,
  loading: () => <FormPlaceholder />,
});

const FormPlaceholder = () => (
  <div
    aria-hidden="true"
    className="flex flex-col gap-3 min-h-[340px] animate-pulse"
  >
    <div className="h-5 w-2/3 mx-auto rounded bg-[#eef2f7]" />
    <div className="h-4 w-1/2 mx-auto rounded bg-[#eef2f7]" />
    <div className="h-11 w-full rounded-lg bg-[#eef2f7] mt-2" />
    <div className="h-11 w-full rounded-lg bg-[#eef2f7]" />
    <div className="h-11 w-full rounded-lg bg-[#eef2f7]" />
    <div className="h-10 w-full rounded-lg bg-[#dce6f5] mt-2" />
  </div>
);

const CAIRA_BADGE =
  "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cpa-new/mobile_banner.webp?tr=orig-true";

interface BannerNewProps {
  pageName: string;
}

const BannerNew: React.FC<BannerNewProps> = ({ pageName }) => {
  const [mountForm, setMountForm] = useState(false);

  useEffect(() => {
    const win = window as Window & {
      requestIdleCallback?: (
        _cb: () => void,
        _opts?: { timeout: number },
      ) => number;
      cancelIdleCallback?: (_id: number) => void;
    };
    if (typeof win.requestIdleCallback === "function") {
      const id = win.requestIdleCallback(() => setMountForm(true), {
        timeout: 2000,
      });
      return () => win.cancelIdleCallback?.(id);
    }
    const id = window.setTimeout(() => setMountForm(true), 1200);
    return () => window.clearTimeout(id);
  }, []);

  return (
    <>
      {/* hero section */}
      <section className="overflow-x-clip banner_gradient max-md:!bg-none max-md:!bg-[#D3E5FD]">
        {/* heroInner — 3-col grid on desktop, flex-col on mobile */}
        <div
          className="main_container grid items-center gap-[14px] min-h-[440px] pt-6 max-md:flex max-md:flex-col max-md:items-stretch max-md:gap-0 max-md:min-h-0 max-md:pt-5 max-md:pb-6 md:px-8 px-4"
          style={{
            gridTemplateColumns:
              "minmax(0,1fr) minmax(290px,1.05fr) minmax(330px,0.95fr)",
          }}
        >
          {/* leftContent */}
          <div className="flex items-start justify-between gap-3 text-left">
            <div className="flex flex-col items-start min-w-0">
              {/* heroTitle */}
              <h1 className="m-0 text-[#333] font-black md:text-[46px] md:leading-[46px] text-[30px] leading-[30px] tracking-[0.6px] md:tracking-[1.38px] md:pl-0 pl-[8px]">
                Be an
                <br />
                <span className="text-[#2a85ff] md:text-[#39f]">
                  AI-Ready CMA
                </span>
                <br />
                in 2026
              </h1>

              {/* integrationPill */}
              <div className="mt-4 md:-ml-3 rounded-[14px] md:rounded-[28px] bg-white md:px-2.5 md:py-2.5 px-2 py-1.5 text-black font-semibold md:text-lg md:leading-[23px] md:tracking-[0px] text-[14px] leading-[18px]">
                U.S. CMA Fully Integrated with
                <br />
                <span className="text-[#2a85ff] md:tracking-[0px]">AI Workflows &amp; Agents</span>
              </div>

              {/* heroSubcopy */}
              <p className="mt-4 mb-0 text-[#333] md:text-sm text-xs leading-[18px] md:pl-0 pl-[8px]">
                <span className="font-semibold">2 Exams | 8 Months</span>
                <br />
                Jobs at MNCs in India, U.S. &amp; Globally
              </p>
            </div>

            {/* mobile CAIRA badge */}
            <img
              src={CAIRA_BADGE}
              alt="Showcase CAIRA Badge on LinkedIn"
              width={125}
              height={194}
              className="md:hidden shrink-0 w-[125px] h-auto"
              decoding="async"
            />
          </div>

          {/* visualWrap — desktop only */}
          <div
            className="relative min-h-[390px] self-stretch max-md:hidden"
            aria-hidden="true"
          >
            <img
              className="pointer-events-none select-none absolute bottom-0 left-[35%] w-[min(105%,425px)] -translate-x-1/2 max-w-none"
              src="https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cpa-new/desktop_banner_new.webp?tr=w-425,q-90,f-webp"
              srcSet="https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cpa-new/desktop_banner_new.webp?tr=w-425,q-90,f-webp 1x, https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cpa-new/desktop_banner_new.webp?tr=w-850,q-90,f-webp 2x"
              alt="AI-Ready CMA in 2026"
              width={425}
              height={382}
              fetchPriority="high"
              decoding="async"
            />
          </div>

          {/* formColumn */}
          <div className="w-full relative flex items-center justify-center lg:justify-end z-[1] max-md:mt-5">
            {/* formCard */}
            <div className="md:w-[400px] w-full p-4 rounded-2xl border border-[#DEE7F5] bg-white">
              {mountForm ? (
                <EnqueryForm
                  pageName={pageName}
                  courseId={pageName === "CPA" ? 1 : 2}
                  darkBackground={false}
                  submitButtonName="Request a Callback  "
                  formType="Banner section"
                  isAStepperForm
                  successMessage={SuccessMessageType.ACCOUNTING}
                  formHeading={FormHeading.TALK_TO_AN_EXPERT}
                />
              ) : (
                <FormPlaceholder />
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BannerNew;
